import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, ArrowLeft, Calendar, ShareNetwork } from '@phosphor-icons/react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { CITY_GUIDES, BLOG_POSTS } from '../data/blogData';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import { NewsletterInline } from '../components/NewsletterPrompts';

const sharePost = async (item, slug) => {
  const url = `${window.location.origin}/blog/${slug}`;
  const title = `${item.title} — World Soccer Party`;
  const text = item.excerpt || item.summary || item.title;
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return;
    }
  } catch (e) { if (e && e.name === 'AbortError') return; }
  try {
    await navigator.clipboard.writeText(`${text} ${url}`);
    toast.success('Link copied — paste it anywhere!');
  } catch { toast.error('Could not share. Copy: ' + url); }
};

const ArticleContent = ({ item, type, slug }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen pt-32 pb-24 bg-gray-50" data-testid="article-page">
      <Helmet>
        <title>{`${item.title} | World Soccer Party`}</title>
        <meta name="description" content={item.excerpt} />
        <meta property="og:title" content={`${item.title} — World Soccer Party`} />
        <meta property="og:description" content={item.excerpt} />
        <meta property="og:image" content={item.image} />
        <meta property="og:type" content="article" />
      </Helmet>
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <button onClick={() => navigate('/blog')} className="inline-flex items-center gap-1 text-[#FF6B35] font-bold text-sm hover:underline" data-testid="back-to-blog">
            <ArrowLeft size={16} weight="bold" /> Back to Blog
          </button>
          <button
            type="button"
            onClick={() => sharePost(item, slug)}
            className="inline-flex items-center gap-1.5 bg-white border-2 border-[#FF6B35] text-[#FF6B35] text-xs font-bold px-4 py-2 rounded-full hover:bg-[#FF6B35] hover:text-white transition-colors"
            data-testid="article-share-btn"
          >
            <ShareNetwork size={14} weight="bold" /> Share
          </button>
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <img src={item.image} alt={item.title} className="w-full h-64 sm:h-80 object-cover rounded-2xl mb-8" />
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {item.tags.map(tag => (
              <span key={tag} className="text-xs bg-[#FF6B35]/10 text-[#FF6B35] px-3 py-1 rounded-full font-bold">{tag}</span>
            ))}
            {item.city && (
              <span className="flex items-center gap-1 text-xs text-gray-500 font-semibold"><MapPin size={12} weight="bold" />{item.city}, {item.country}</span>
            )}
            {item.date && (
              <span className="flex items-center gap-1 text-xs text-gray-500"><Calendar size={12} weight="bold" />{item.date}</span>
            )}
          </div>
          <h1 className="font-['Bebas_Neue'] text-4xl sm:text-5xl uppercase text-gray-900 mb-6" data-testid="article-title">{item.title}</h1>
          <div className="prose max-w-none">
            {item.content.map((block, i) => {
              if (block.type === 'heading') return <h2 key={i} className="font-['Bebas_Neue'] text-2xl uppercase text-gray-900 mt-8 mb-3">{block.text}</h2>;
              if (block.type === 'paragraph') return <p key={i} className="text-gray-600 text-base leading-relaxed mb-4">{block.text}</p>;
              if (block.type === 'cta') return (
                <div key={i} className="mt-8 mb-4">
                  {block.link.startsWith('http') ? (
                    <a href={block.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-6 py-3 font-bold uppercase rounded-lg hover:shadow-lg transition-all text-sm" data-testid="article-cta">
                      {block.text} <ArrowRight size={16} weight="bold" />
                    </a>
                  ) : (
                    <Link to={block.link} className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-6 py-3 font-bold uppercase rounded-lg hover:shadow-lg transition-all text-sm" data-testid="article-cta">
                      {block.text} <ArrowRight size={16} weight="bold" />
                    </Link>
                  )}
                </div>
              );
              return null;
            })}
            {/* Newsletter prompt at bottom of article */}
            <div className="mt-10 border-t border-gray-200 pt-8">
              <NewsletterInline
                hook="Hungry? Get the full list of 40+ Food Trucks and their daily 'WSP Specials' sent to your inbox."
                buttonText="Send It"
                testId="blog-newsletter"
                source="Blog Fan"
                className="bg-orange-50 border border-orange-200 rounded-xl p-5"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export const BlogPage = () => {
  const { slug } = useParams();
  const [dynamicPosts, setDynamicPosts] = useState([]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts?limit=200`)
      .then(r => setDynamicPosts(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, []);

  if (slug) {
    const guide = CITY_GUIDES.find(g => g.slug === slug);
    const post = BLOG_POSTS.find(p => p.slug === slug);
    // Check dynamic posts by slug or id
    const dynPost = dynamicPosts.find(p => p.slug === slug || p.id === slug);
    
    // If the DB has this post and it's hidden/draft, don't show it
    if (dynPost && (dynPost.hidden || dynPost.status === 'draft')) {
      return <div className="min-h-screen pt-32 pb-24 bg-gray-50 text-center"><p className="text-gray-500 mt-20">This post is currently unavailable.</p></div>;
    }
    
    if (dynPost) {
      const item = {
        title: dynPost.title,
        excerpt: dynPost.summary || '',
        image: dynPost.image_url || '',
        tags: dynPost.seo_tags ? dynPost.seo_tags.split(',').map(t => t.trim()) : [],
        date: dynPost.published_date,
        content: dynPost.content ? [{ type: 'paragraph', text: dynPost.content }] : [],
      };
      // If there's also a hardcoded version with full content, merge
      if (post && post.content) {
        item.content = post.content;
        item.image = item.image || post.image;
        item.tags = item.tags.length > 0 ? item.tags : post.tags;
        item.date = item.date || post.date;
      } else if (guide && guide.content) {
        item.content = guide.content;
        item.image = item.image || guide.image;
        item.tags = item.tags.length > 0 ? item.tags : guide.tags;
      }
      return <ArticleContent item={item} type="post" slug={slug} />;
    }
    // Check if DB says this slug is hidden
    const dbRecord = dynamicPosts.find(p => p.slug === slug);
    if (dbRecord && (dbRecord.hidden || dbRecord.status === 'draft')) {
      return <div className="min-h-screen pt-32 pb-24 bg-gray-50 text-center"><p className="text-gray-500 mt-20">This post is currently unavailable.</p></div>;
    }
    const item = guide || post;
    if (item) return <ArticleContent item={item} type={guide ? 'guide' : 'post'} slug={slug} />;
  }

  return (
    <div className="min-h-screen pt-32 pb-24 bg-gray-50" data-testid="blog-page">
      <Helmet>
        <title>{'Blog & City Guides | World Soccer Party'}</title>
        <meta name="description" content="Your insider guide to World Soccer 2026 host cities, events, and the best viewing experiences across Canada, USA, and Mexico." />
        <meta property="og:title" content="Blog & City Guides — World Soccer Party 2026" />
        <meta property="og:description" content="Your insider guide to World Soccer 2026 host cities and viewing experiences." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <h1 className="font-['Bebas_Neue'] text-5xl sm:text-6xl tracking-tight uppercase text-gray-900 mb-4">Blog & City Guides</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">Your insider guide to World Soccer 2026 host cities, events, and the best viewing experiences</p>
        </motion.div>

        {/* City Guides */}
        <div className="mb-20">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-['Bebas_Neue'] text-3xl sm:text-4xl uppercase text-gray-900">City Guides</h2>
            <span className="text-sm text-gray-500 font-semibold">{CITY_GUIDES.length} guides</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CITY_GUIDES.filter(guide => {
              const dbVersion = dynamicPosts.find(d => d.slug === guide.slug);
              if (dbVersion && (dbVersion.hidden || dbVersion.status === 'draft')) return false;
              return true;
            }).map((guide, index) => (
              <motion.div key={guide.slug} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.08 }} className="relative">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); sharePost(guide, guide.slug); }}
                  className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-white/95 backdrop-blur border-2 border-[#FF6B35] text-[#FF6B35] text-xs font-bold px-2.5 py-1 rounded-full hover:bg-[#FF6B35] hover:text-white transition-colors shadow"
                  data-testid={`guide-share-${guide.slug}`}
                  aria-label={`Share ${guide.title}`}
                ><ShareNetwork size={12} weight="bold" /> Share</button>
                <Link to={`/blog/${guide.slug}`} className="block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100 group" data-testid={`guide-card-${guide.slug}`}>
                  <div className="relative overflow-hidden">
                    <img src={guide.image} alt={guide.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                      <div className="flex items-center gap-2 text-white"><MapPin size={16} weight="bold" /><span className="font-bold text-sm">{guide.city}, {guide.country}</span></div>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex gap-2 mb-3">{guide.tags.map(tag => <span key={tag} className="text-xs bg-[#FF6B35]/10 text-[#FF6B35] px-2 py-1 rounded-full font-bold">{tag}</span>)}</div>
                    <h3 className="font-['Bebas_Neue'] text-2xl uppercase text-gray-900 mb-2">{guide.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{guide.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-[#FF6B35] font-bold text-sm group-hover:gap-2 transition-all">Read Guide <ArrowRight size={16} weight="bold" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Blog Posts */}
        <div>
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-['Bebas_Neue'] text-3xl sm:text-4xl uppercase text-gray-900">Latest Posts</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Dynamic posts from admin */}
            {dynamicPosts.map((post, index) => (
              <motion.div key={post.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} className="relative">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); sharePost({ title: post.title, excerpt: post.summary }, post.slug || post.id); }}
                  className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-white/95 backdrop-blur border-2 border-[#FF6B35] text-[#FF6B35] text-xs font-bold px-2.5 py-1 rounded-full hover:bg-[#FF6B35] hover:text-white transition-colors shadow"
                  data-testid={`blog-share-${post.id}`}
                  aria-label={`Share ${post.title}`}
                ><ShareNetwork size={12} weight="bold" /> Share</button>
                <Link to={`/blog/${post.id}`} className="block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100 group" data-testid={`blog-card-${post.id}`}>
                  {post.image_url && (
                    <div className="relative overflow-hidden">
                      <img src={post.image_url} alt={post.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-1 text-gray-500 text-xs"><Calendar size={12} weight="bold" />{post.published_date}</div>
                      {post.author && <span className="text-xs text-gray-500">by {post.author}</span>}
                    </div>
                    {post.seo_tags && <div className="flex flex-wrap gap-1 mb-2">{post.seo_tags.split(',').slice(0, 3).map(tag => <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{tag.trim()}</span>)}</div>}
                    <h3 className="font-['Bebas_Neue'] text-xl uppercase text-gray-900 mb-2">{post.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">{post.summary}</p>
                    <span className="inline-flex items-center gap-1 text-[#FF6B35] font-bold text-sm group-hover:gap-2 transition-all">Read More <ArrowRight size={16} weight="bold" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
            {/* Static blog posts — filtered by DB visibility */}
            {BLOG_POSTS.filter(post => {
              const dbVersion = dynamicPosts.find(d => d.slug === post.slug);
              if (dbVersion && (dbVersion.hidden || dbVersion.status === 'draft')) return false;
              return true;
            }).map((post, index) => (
              <motion.div key={post.slug} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} className="relative">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); sharePost(post, post.slug); }}
                  className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-white/95 backdrop-blur border-2 border-[#FF6B35] text-[#FF6B35] text-xs font-bold px-2.5 py-1 rounded-full hover:bg-[#FF6B35] hover:text-white transition-colors shadow"
                  data-testid={`blog-share-${post.slug}`}
                  aria-label={`Share ${post.title}`}
                ><ShareNetwork size={12} weight="bold" /> Share</button>
                <Link to={`/blog/${post.slug}`} className="block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100 group" data-testid={`blog-card-${post.slug}`}>
                  <div className="relative overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-1 text-gray-500 text-xs"><Calendar size={12} weight="bold" />{post.date}</div>
                      {post.tags.map(tag => <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>)}
                    </div>
                    <h3 className="font-['Bebas_Neue'] text-xl uppercase text-gray-900 mb-2">{post.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-[#FF6B35] font-bold text-sm group-hover:gap-2 transition-all">Read More <ArrowRight size={16} weight="bold" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}
          className="mt-20 text-center bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] rounded-2xl p-12 text-white">
          <h2 className="font-['Bebas_Neue'] text-4xl uppercase mb-4">Got a Story to Share?</h2>
          <p className="text-lg mb-6 opacity-90">Know the best spot in your city? Write for us and help fans find their match.</p>
          <a href="mailto:send-as+info@wsp-partners.com" className="inline-block bg-white text-[#FF6B35] px-8 py-4 font-bold uppercase tracking-wider hover:shadow-lg transition-all rounded-lg">Contact Us</a>
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPage;
